import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { Header, Page, Content, Progress } from '@backstage/core-components';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntitySearchBar,
  EntityTagPicker,
  UserListPicker,
  catalogApiRef,
  entityPresentationApiRef,
  useEntityTypeFilter,
} from '@backstage/plugin-catalog-react';
import type { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import {
  ScaffolderPageContextMenu,
  TemplateGroups,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  createScaffolderFieldExtension,
  ScaffolderFieldExtensions,
} from '@backstage/plugin-scaffolder-react';
import {
  ApplicationMetadataField,
  ApplicationMetadataFieldSchema,
  ApplicationNameField,
  ApplicationNameFieldSchema,
  AzureEntraUserPicker,
  AzureEntraUserPickerSchema,
  BudgetSeriesField,
  BudgetSeriesFieldSchema,
  EntityDescription,
  EntityDescriptionSchema,
  FoundryModelPicker,
  FoundryModelPickerSchema,
  InvoiceSectionPicker,
  InvoiceSectionPickerSchema,
  IpRangePicker,
  IpRangePickerSchema,
  validateIpRange,
  validateFoundryModel,
} from '@internal/backstage-plugin-scaffolder-field-extensions';
import { EntityPickerFieldSchema } from '@backstage/plugin-scaffolder';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useAsync } from 'react-use';
import { useNavigate } from 'react-router-dom';

type ScaffolderEntity = { spec?: { type?: unknown } };
type ScaffolderGroup = {
  title: string;
  filter: (entity: ScaffolderEntity) => boolean;
};
type ScaffolderTemplate = { spec?: { type?: string } };

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const buildCatalogFilter = (uiSchema: any) => {
  const options = uiSchema?.['ui:options'] ?? {};
  const catalogFilter =
    options.catalogFilter ??
    options.allowedKinds?.map((kind: string) => ({ kind }));
  if (!catalogFilter) return undefined;
  return Array.isArray(catalogFilter) ? catalogFilter : [catalogFilter];
};

const EntityPickerWithTitle = (props: any) => {
  const {
    onChange,
    schema: { title = 'Entity', description } = {},
    required,
    uiSchema = {},
    rawErrors,
    formData,
    idSchema,
  } = props;
  const catalogApi = useApi(catalogApiRef);
  const presentationApi = useApi(entityPresentationApiRef);
  const defaultKind = uiSchema['ui:options']?.defaultKind;
  const defaultNamespace =
    uiSchema['ui:options']?.defaultNamespace || undefined;
  const allowArbitraryValues =
    uiSchema['ui:options']?.allowArbitraryValues ?? true;
  const displayAnnotation = uiSchema['ui:options']?.displayAnnotation;
  const catalogFilter = buildCatalogFilter(uiSchema);

  const { value: entities, loading } = useAsync(async () => {
    const fields = [
      'kind',
      'metadata.name',
      'metadata.namespace',
      'metadata.title',
      'metadata.annotations',
      'spec.profile.displayName',
      'spec.type',
    ];
    const { items } = await catalogApi.getEntities(
      catalogFilter ? { filter: catalogFilter, fields } : { fields },
    );
    const presentationMap = new Map(
      items.map(item => {
        const ref = stringifyEntityRef(item);
        const primaryTitle =
          (displayAnnotation &&
            (item as any).metadata?.annotations?.[displayAnnotation]) ||
          (item as any).metadata?.title ||
          (item as any).spec?.profile?.displayName ||
          item.metadata?.name ||
          ref;
        return [ref, { primaryTitle }] as const;
      }),
    );
    return { items, presentationMap };
  }, [catalogApi, presentationApi, displayAnnotation]);

  const selectedEntity =
    entities?.items.find(e => stringifyEntityRef(e) === formData) ??
    (allowArbitraryValues && formData ? formData : null);

  return (
    <Autocomplete
      id={idSchema?.$id}
      value={selectedEntity}
      loading={loading}
      onChange={(_, option, reason) => {
        if (typeof option !== 'string') {
          onChange(option ? stringifyEntityRef(option) : undefined);
        } else if (reason === 'blur' || reason === 'create-option') {
          let ref = option;
          try {
            ref = stringifyEntityRef(
              parseEntityRef(option, { defaultKind, defaultNamespace }),
            );
          } catch {
            // ignore parse errors for free-form input
          }
          onChange(ref || undefined);
        }
      }}
      options={entities?.items || []}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;
        const ref = stringifyEntityRef(option);
        return entities?.presentationMap.get(ref)?.primaryTitle || ref;
      }}
      freeSolo={allowArbitraryValues}
      autoSelect
      renderInput={params => (
        <TextField
          {...params}
          label={title}
          margin="dense"
          helperText={description}
          FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
          variant="outlined"
          required={required}
          InputProps={{ ...params.InputProps, autoComplete: 'off' }}
          error={rawErrors?.length > 0 && !formData}
        />
      )}
    />
  );
};

const EntityPickerWithTitleSchema = EntityPickerFieldSchema;

const EntityPickerFieldExtension = createScaffolderFieldExtension({
  name: 'EntityPicker',
  component: EntityPickerWithTitle,
  schema: EntityPickerWithTitleSchema as any,
});

const EntityPickerField = (EntityPickerFieldExtension as any).expose();

const scaffolderFieldExtensions = [
  createFormField({
    name: 'BudgetSeriesField',
    component: BudgetSeriesField as any,
    schema: BudgetSeriesFieldSchema as any,
  }),
  createFormField({
    name: 'ApplicationMetadataField',
    component: ApplicationMetadataField as any,
    schema: ApplicationMetadataFieldSchema as any,
  }),
  createFormField({
    name: 'ApplicationNameField',
    component: ApplicationNameField as any,
    schema: ApplicationNameFieldSchema as any,
  }),
  createFormField({
    name: 'AzureEntraUserPicker',
    component: AzureEntraUserPicker as any,
    schema: AzureEntraUserPickerSchema as any,
  }),
  createFormField({
    name: 'AzureGraphUserPickerField',
    component: AzureEntraUserPicker as any,
    schema: AzureEntraUserPickerSchema as any,
  }),
  createFormField({
    name: 'InvoiceSectionPicker',
    component: InvoiceSectionPicker as any,
    schema: InvoiceSectionPickerSchema as any,
  }),
  createFormField({
    name: 'FoundryModelPicker',
    component: FoundryModelPicker as any,
    schema: FoundryModelPickerSchema as any,
    validation: validateFoundryModel,
  }),
  createFormField({
    name: 'EntityDescription',
    component: EntityDescription as any,
    schema: EntityDescriptionSchema as any,
  }),
  createFormField({
    name: 'IpRangePicker',
    component: IpRangePicker as any,
    schema: IpRangePickerSchema as any,
    validation: validateIpRange,
  }),
];

const TemplateCategoryPickerWithTitle = () => {
  const { error, loading, availableTypes, selectedTypes, setSelectedTypes } =
    useEntityTypeFilter();

  if (loading) {
    return <Progress />;
  }

  if (error || !availableTypes) {
    return null;
  }

  return (
    <Box pb={1} pt={1}>
      <Typography
        variant="button"
        component="label"
        htmlFor="categories-picker"
      >
        Categories
      </Typography>
      <Autocomplete
        id="categories-picker"
        multiple
        options={availableTypes}
        value={selectedTypes}
        onChange={(_, value) => setSelectedTypes(value)}
        renderOption={(option, { selected }) => (
          <FormControlLabel
            control={
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
              />
            }
            label={option}
          />
        )}
        size="small"
        popupIcon={<ExpandMoreIcon data-testid="categories-picker-expand" />}
        renderInput={params => <TextField {...params} variant="outlined" />}
      />
    </Box>
  );
};

const useScaffolderGroups = (): ScaffolderGroup[] => {
  const catalogApi = useApi(catalogApiRef);
  const { value } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'template' },
    });

    const categories = (response.items ?? [])
      .map(entity => (entity as ScaffolderTemplate)?.spec?.type?.trim())
      .filter((type): type is string => Boolean(type));

    return Array.from(new Set(categories));
  }, [catalogApi]);

  const categories = value ?? [];

  return [
    ...categories.map(category => ({
      title: category,
      filter: (entity: ScaffolderEntity) => {
        const type = entity?.spec?.type;
        return typeof type === 'string' && type.trim() === category;
      },
    })),
    {
      title: 'Others',
      filter: (entity: ScaffolderEntity) => {
        const type = entity?.spec?.type;
        const normalizedType = typeof type === 'string' ? type.trim() : '';
        return !categories.includes(normalizedType);
      },
    },
  ];
};

const ScaffolderTemplateListPageWithTitleCategory = ({
  groups,
  templateFilter,
  TemplateCardComponent,
  headerOptions,
  contextMenu,
}: TemplateListPageProps) => {
  const navigate = useNavigate();
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const editorRoute = useRouteRef(scaffolderPlugin.routes.edit);
  const actionsRoute = useRouteRef(scaffolderPlugin.routes.actions);
  const tasksRoute = useRouteRef(scaffolderPlugin.routes.listTasks);
  const templatingExtensionsRoute = useRouteRef(
    scaffolderPlugin.routes.templatingExtensions,
  );

  const groupList = groups?.length
    ? [
        ...groups,
        {
          title: 'Others',
          filter: (entity: ScaffolderEntity) =>
            !groups.some(group => group.filter(entity as any)),
        },
      ]
    : [{ title: 'Templates', filter: () => true }];

  const scaffolderPageContextMenuProps = {
    onEditorClicked:
      contextMenu?.editor !== false ? () => navigate(editorRoute()) : undefined,
    onActionsClicked:
      contextMenu?.actions !== false
        ? () => navigate(actionsRoute())
        : undefined,
    onTasksClicked:
      contextMenu?.tasks !== false ? () => navigate(tasksRoute()) : undefined,
    onTemplatingExtensionsClicked:
      contextMenu?.templatingExtensions !== false
        ? () => navigate(templatingExtensionsRoute())
        : undefined,
  };

  return (
    <EntityListProvider>
      <Page themeId="home">
        <Header
          pageTitleOverride="Create a new component"
          title="Create a new component"
          subtitle="Create new software components using standard templates in your organization"
          {...headerOptions}
        >
          <ScaffolderPageContextMenu {...scaffolderPageContextMenuProps} />
        </Header>
        <Content>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker initialFilter="template" hidden />
              <UserListPicker
                initialFilter="all"
                availableFilters={['all', 'starred']}
              />
              <TemplateCategoryPickerWithTitle />
              <EntityTagPicker />
              <EntityOwnerPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <TemplateGroups
                groups={groupList}
                templateFilter={templateFilter}
                TemplateCardComponent={TemplateCardComponent}
                onTemplateSelected={template => {
                  const { namespace, name } = parseEntityRef(
                    stringifyEntityRef(template),
                  );
                  navigate(templateRoute({ namespace, templateName: name }));
                }}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </Page>
    </EntityListProvider>
  );
};

const ScaffolderPageWithDynamicGroups = ({
  ScaffolderPage,
}: {
  ScaffolderPage: (props: {
    groups: ScaffolderGroup[];
    formFields?: any[];
    children?: React.ReactNode;
    components?: {
      EXPERIMENTAL_TemplateListPageComponent?: (
        props: TemplateListPageProps,
      ) => JSX.Element;
    };
  }) => JSX.Element;
}) => {
  const groups = useScaffolderGroups();

  return (
    <ScaffolderPage
      groups={groups}
      formFields={scaffolderFieldExtensions}
      components={{
        EXPERIMENTAL_TemplateListPageComponent:
          ScaffolderTemplateListPageWithTitleCategory,
      }}
    >
      <ScaffolderFieldExtensions>
        <EntityPickerField />
      </ScaffolderFieldExtensions>
    </ScaffolderPage>
  );
};

export const essityScaffolderOverridesModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    scaffolderPlugin.getExtension('page:scaffolder').override({
      factory(originalFactory) {
        return originalFactory({
          params: {
            loader: async () => {
              return import('@backstage/plugin-scaffolder').then(m => (
                <ScaffolderPageWithDynamicGroups
                  ScaffolderPage={m.ScaffolderPage}
                />
              ));
            },
          },
        });
      },
    }),
  ],
});
